import { useCallback, useState } from 'react';
import type { Card, PlaceSubmit, Row } from '@/lib/qcp/protocol';

/** A single pending card + target row, not yet submitted to the server. */
export interface PendingPlacement {
  card: Card;
  row: Row;
}

export interface OfcPlacementState {
  /** Index of the currently selected draw card (null = none). */
  selectedIndex: number | null;
  /** Select a draw card by its index in the current draw array. */
  select: (index: number) => void;
  /**
   * Place the currently selected draw card onto `row`.
   *
   * - Round 0 (initial deal of 5): places all 5 freely, no discard.
   * - Rounds 1-4 (Pineapple draw-3): the first 2 placed cards go to `pending`;
   *   when 2 placements are made the remaining (3rd) draw card that has not
   *   been placed becomes `discard` automatically.
   *
   * No-op if no card is selected or if the round's quota is already filled.
   * Slot-capacity and poker-legality are NOT enforced client-side — the
   * server is authoritative. The hook only tracks selection + pending count.
   */
  placeTo: (row: Row) => void;
  /** Remove a pending card (tap it on the board to change your mind this hand). */
  unplace: (card: Card) => void;
  /** Move a pending card to a different row (re-assign its row this hand). */
  moveTo: (card: Card, row: Row) => void;
  /** Cards placed so far (card + target row), not yet submitted. */
  pending: PendingPlacement[];
  /**
   * The card that will be discarded (auto-determined in rounds 1-4 once 2
   * cards have been placed — the sole remaining draw card). Null in round 0
   * and until enough placements are made.
   */
  discard: Card | null;
  /**
   * True when the round's placement is complete:
   * - Round 0: all 5 draw cards have been placed.
   * - Rounds 1-4: exactly 2 draw cards have been placed (1 auto-discard).
   */
  isComplete: boolean;
  /** Reset all local state (call at round start or after server ack). */
  clear: () => void;
  /**
   * Build the `place:submit` payload for this round.
   * Returns null if placement is not yet complete.
   */
  buildSubmit: (matchId: string) => PlaceSubmit | null;
}

/**
 * Local pending-placement state for one OFC round.
 *
 * `round`  — server round index (0 = opening deal-5; 1-4 = Pineapple draw-3).
 * `draw`   — current draw cards for this round (5 cards in round 0, 3 in 1-4).
 *
 * Placement rules (client-side tracking only, server validates):
 *   Round 0  → place all 5 cards, no discard. isComplete when pending.length === 5.
 *   Rounds 1-4 → place exactly 2 of 3 draw cards; the unplaced 3rd is discard.
 *                isComplete when pending.length === 2.
 *
 * The hook never validates poker hand legality or slot capacity — those checks
 * belong to the server (QChinesePoker/apps/qcp-gameserver). Client UI may use
 * `isComplete` and `discard` to enable the submit button and display the
 * to-be-discarded card.
 */
/** Fantasyland per-row capacities (front 3 / middle 5 / back 5 = 13 placed). */
const FL_CAPS: Record<Row, number> = { front: 3, middle: 5, back: 5 };

export function useOfcPlacement(round: number, draw: Card[], flCount = 0): OfcPlacementState {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [pending, setPending] = useState<PendingPlacement[]>([]);

  // ── Derived values ───────────────────────────────────────────────────────

  const isRound0 = round === 0;
  // Fantasyland: the seat sets its whole 13-card board (front 3 / middle 5 /
  // back 5) in ONE placement from a 14-17 card draw; the leftover (1-4) is the
  // discard. flCount > 0 signals this seat is in FL for the current hand.
  const isFl = flCount > 0;
  const rowCount = (row: Row): number => pending.filter((p) => p.row === row).length;
  // FL: place 13 (3/5/5). Round 0: place all 5. Pineapple rounds: place 2.
  const placementQuota = isFl ? 13 : isRound0 ? 5 : 2;
  const isComplete = isFl
    ? rowCount('front') === 3 && rowCount('middle') === 5 && rowCount('back') === 5
    : pending.length === placementQuota;

  // In Pineapple rounds: once 2 placements are made the last remaining draw
  // card (not used in any pending placement) is the auto-discard.
  let discard: Card | null = null;
  if (!isRound0 && pending.length >= 2) {
    const usedCards = new Set(pending.map((p) => cardKey(p.card)));
    const remaining = draw.filter((c) => !usedCards.has(cardKey(c)));
    discard = remaining[0] ?? null;
  }

  // ── Actions ──────────────────────────────────────────────────────────────

  const select = useCallback((index: number) => {
    setSelectedIndex((cur) => (cur === index ? null : index));
  }, []);

  const placeTo = useCallback(
    (row: Row) => {
      if (selectedIndex === null) return;
      if (pending.length >= placementQuota) return;
      // FL: enforce per-row capacity (front 3 / middle 5 / back 5).
      if (isFl && pending.filter((p) => p.row === row).length >= FL_CAPS[row]) return;

      const card = draw[selectedIndex];
      if (card === undefined) return;

      // Don't place the same draw-card index twice.
      const alreadyPlacedIndices = new Set(
        pending.map((p) => draw.findIndex((d) => cardKey(d) === cardKey(p.card))),
      );
      if (alreadyPlacedIndices.has(selectedIndex)) return;

      setPending((prev) => [...prev, { card, row }]);
      setSelectedIndex(null);
    },
    [selectedIndex, pending, draw, placementQuota, isFl],
  );

  const unplace = useCallback((card: Card) => {
    const key = cardKey(card);
    setPending((prev) => prev.filter((p) => cardKey(p.card) !== key));
    setSelectedIndex(null);
  }, []);

  const moveTo = useCallback((card: Card, row: Row) => {
    const key = cardKey(card);
    setPending((prev) => prev.map((p) => (cardKey(p.card) === key ? { card: p.card, row } : p)));
    setSelectedIndex(null);
  }, []);

  const clear = useCallback(() => {
    setSelectedIndex(null);
    setPending([]);
  }, []);

  const buildSubmit = useCallback(
    (matchId: string): PlaceSubmit | null => {
      if (!isComplete) return null;
      return {
        matchId,
        placements: pending.map(({ card, row }) => ({ card, row })),
        discard,
      };
    },
    [isComplete, pending, discard],
  );

  return {
    selectedIndex,
    select,
    placeTo,
    unplace,
    moveTo,
    pending,
    discard,
    isComplete,
    clear,
    buildSubmit,
  };
}

/** Stable key for a Card — used for identity comparisons within a draw round. */
function cardKey(c: Card): string {
  return `${c.r}${c.s}`;
}
